/**
 * One poller for the document redaction job.
 *
 * OCR and PII are two tasks in one job, so one request per tick feeds both. Two
 * pollers would observe the job at different instants and disagree.
 *
 * DISCOVER ONCE, THEN POLL BY ID — the shape the media editor already uses
 * (`fetchTdo` reads the TDO's whole task list at load; the engine sagas then
 * poll `job(id)`). On open, one lookup finds what each engine has already
 * produced and which task to watch; after that each tick is two `task(id)`
 * reads. It used to re-read every task on the TDO plus thirty jobs and their
 * task lists, every ten seconds, to learn two statuses.
 *
 * The cheapest case costs nothing at all: where both engines' result assets are
 * already on the TDO — any document opened after its ingestion finished — the
 * watch settles on the discovery read and never polls. See `EngineWork`.
 *
 * Shared: the transport and the clock. NOT shared: the meaning — each engine has
 * its own fallback when a read fails (OCR `indeterminate`, PII `not-run`), and
 * those two apply only while no task has been found. Once one has, an outage is
 * not evidence about the run: the budget grows, the retries back off, and
 * giving up reports `timed-out` rather than claiming a live job never ran.
 *
 * ONE WATCH PER DOCUMENT, and everything it reacts to is scoped to that
 * document's `tdoId`. The workspace keeps several open and switching is one
 * click, so a run started elsewhere is not this watch's business: picking one
 * up would revive an engine against a job that is not on this TDO, fabricating
 * a failure or burning the whole task budget waiting for a task that cannot
 * appear. The scope is enforced at the `actionChannel` pattern, so a foreign
 * re-run is never even buffered.
 *
 * Nothing here may throw out of the saga or end it early. `documentsSaga` is
 * always-on under `keepAlive`, so an escaping throw restarts every document
 * saga; and this is the only watcher of PII re-runs, so returning strands their
 * spinner. Hence: each step fenced separately, a failed read retried, and giving
 * up settles the engines so the loop parks on the rerun channel.
 *
 * Settling the watch is not settling the UI — `ocrEngineState` /
 * `piiEngineState` live in redux. Fallbacks therefore fire on the first failure
 * and again when giving up.
 */
import {
  actionChannel,
  call,
  cancelled,
  delay,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'typed-redux-saga/macro';

import { TDOId } from '@common-modules/universal/models/Brands';
import { selectConfigEngines } from '@common-modules/engines/selectors';

import {
  CLOSE_DOCUMENT,
  OCR_TIMED_OUT,
  OPEN_DOCUMENT_SUCCESS,
  PII_TIMED_OUT,
  RUN_OCR_DETECTION_SUCCESS,
  RUN_PII_DETECTION_SUCCESS,
} from '../actions';
import { selectSession } from '../selectors';
import {
  EngineDiscovery,
  engineStatusFromWork,
  serviceDiscoverEngineWork,
  serviceFetchTaskStatuses,
} from '../services';
import {
  initialOcrWatchState,
  OcrWatchState,
  resolveOcrUnconfigured,
  stepOcrWatch,
  stepOcrWatchError,
} from './ocrStatus';
import {
  initialPiiWatchState,
  PiiWatchState,
  resolvePiiUnavailable,
  stepPiiWatch,
  stepPiiWatchError,
} from './runPii';
import { selectGraphQLConfig, selectToken } from './auth';

/**
 * How often the job is re-read while any engine is working. No overall cap,
 * matching every other poller here — the loop ends with the work, the document
 * closing, or a reopen. The one bounded wait is per-engine, in the step
 * functions, for a job that never shows its task.
 */
const POLL_INTERVAL_MS = 10_000;

/**
 * Consecutive failed reads before the watch gives up, when it has NOT yet found
 * a task to watch (~30 s at the interval).
 */
const MAX_CONSECUTIVE_FETCH_ERRORS = 3;

/**
 * The budget once a task IS known.
 *
 * Thirty seconds of API trouble is not evidence that a run ended, and giving up
 * used to resolve PII to `not-run` — which puts "Run PII Detection" back in
 * front of the reviewer for a job still running, and starts a second one on top
 * of it. A task id in hand means a real run exists, so the watch waits out a
 * far longer outage before saying anything about it. With the backoff below,
 * ten attempts span roughly twenty minutes.
 */
const MAX_CONSECUTIVE_FETCH_ERRORS_WITH_TASK = 10;

/** Ceiling for the backoff, so a long outage still re-checks every few minutes. */
const MAX_RETRY_BACKOFF_MS = 300_000;

/**
 * How long to wait after `n` consecutive failed reads.
 *
 * Exponential, because retrying a down API every 10 s for twenty minutes is
 * load the API does not need from every open editor at once, and JITTERED for
 * the same reason: an outage synchronises every client in the org onto the same
 * schedule, and they then retry in a thundering herd on the same tick. Equal
 * jitter (half fixed, half random) keeps the growth predictable while spreading
 * the arrivals.
 *
 * Exported for its bounds test — the invariant that matters is that it never
 * returns less than a poll interval's worth of patience, and never more than
 * the ceiling.
 */
export function retryBackoffMs(
  consecutiveErrors: number,
  random: () => number = Math.random
): number {
  const ceiling = Math.min(
    POLL_INTERVAL_MS * 2 ** Math.max(0, consecutiveErrors - 1),
    MAX_RETRY_BACKOFF_MS
  );
  return Math.round(ceiling / 2 + random() * (ceiling / 2));
}

/** An engine with nothing found for it — the seed for a freshly created task. */
const NO_WORK: EngineDiscovery['ocr'] = {
  resultExists: false,
  taskId: null,
  status: null,
  createdDateTime: null,
};

/** Either re-run action, read for the task ids the create response returned. */
interface RerunAction {
  payload?: { withOcr?: boolean; ocrTaskId?: string; piiTaskId?: string };
}

const SETTLED: OcrWatchState & PiiWatchState = {
  active: false,
  sawTask: false,
  pollsAwaitingTask: 0,
};

/**
 * Watch every engine on this document until each has settled, then park on a
 * `take` so a re-run rejoins this loop rather than starting a second poller.
 * The re-run comes over an `actionChannel` so it is not missed mid-fetch.
 */
export function* watchEngineStatus({
  payload: { tdoId },
}: ReturnType<typeof OPEN_DOCUMENT_SUCCESS>) {
  const { documentOcrEngineId, documentPIIDetectionEngineId } =
    yield* select(selectConfigEngines);
  const session = yield* select(selectSession);
  const pdfUrl = session?.pdfUrl ?? null;

  const ocrEngineId = documentOcrEngineId || null;
  // PII needs a PDF to map its results onto, so no url means it cannot run.
  const piiEngineId =
    documentPIIDetectionEngineId && pdfUrl
      ? documentPIIDetectionEngineId
      : null;

  // `checking` must never outlive this saga's first pass.
  if (!ocrEngineId) {
    yield* resolveOcrUnconfigured(tdoId);
  }
  if (!piiEngineId) {
    yield* resolvePiiUnavailable(tdoId);
  }

  // Both re-runs come over ONE channel so neither is missed mid-fetch, and so
  // a watch that has parked wakes for either.
  //
  // SCOPED TO THIS DOCUMENT, in the pattern rather than after the take: the
  // workspace keeps several documents open, and a run started on another one
  // must not be buffered here at all — waking this watch for it would revive
  // an engine against a job that is not on this TDO, which either fabricates a
  // failure or burns the full ~2 min task budget waiting for a task that can
  // never appear. Matching on type alone is what made that reachable in one
  // click.
  const isRerunForThisDocument = (a: {
    type: string;
    payload?: { tdoId?: TDOId };
  }) =>
    (a.type === RUN_OCR_DETECTION_SUCCESS.type ||
      a.type === RUN_PII_DETECTION_SUCCESS.type) &&
    a.payload?.tdoId === tdoId;
  const rerunChannel = yield* actionChannel(isRerunForThisDocument);

  let ocr: OcrWatchState = ocrEngineId ? initialOcrWatchState() : SETTLED;
  let pii: PiiWatchState = piiEngineId ? initialPiiWatchState() : SETTLED;
  let ocrFirstReading = true;
  let piiFirstReading = true;
  // Whether this engine's failure fallback has been dispatched. Distinct from
  // `firstReading`: a failed read takes no reading, so it must not consume the
  // first one — doing so sent a later terminal task down the `!firstReading`
  // branch, which offers a reload instead of loading the manifest.
  let ocrFallbackAnnounced = false;
  let piiFallbackAnnounced = false;
  let started = false;
  let consecutiveFetchErrors = 0;
  /** What the open-time lookup found; null forces a fresh one next tick. */
  let discovery: EngineDiscovery | null = null;
  /**
   * Whether PII's existing result asset may still stand in for its task status.
   *
   * False from the moment a re-run starts: that asset is the PREVIOUS run's,
   * and trusting it would report the new run complete before it began.
   */
  let trustPiiResult = true;
  /**
   * The OCR counterpart, and the reason it exists: discovery checks RESULTS
   * FIRST and returns before reading tasks, so a re-run on a document that
   * already has a manifest never finds its new task at all — the stale asset
   * reports `complete` and the real job finishes into silence. A failed run
   * keeps its partial manifest, which is exactly the population Run Again is
   * offered to. `engineStatusFromWork` preferring a task over an asset does not
   * save us here; there is no task to prefer.
   */
  let trustOcrResult = true;
  /**
   * Whether the file this session opened IS OCR's own output.
   *
   * The question a settled OCR task cannot answer on its own: the manifest
   * describes the searchable copy OCR republishes, so reporting `done` is only
   * honest when that copy is what the document is rendering. Seeded from the
   * asset `openDocument` resolved — OCR stamps its engine id on what it
   * publishes, the uploaded original carries none — and cleared the moment a
   * re-run starts, because whatever the session pinned predates it.
   *
   * Replaces `firstReading` in that role, which was a proxy for it and wrong in
   * both directions. See `stepOcrWatch`.
   */
  let ocrPinnedOutput =
    !!ocrEngineId && session?.mediaAssetEngineId === ocrEngineId;
  /** The `media` asset this session opened; compared with the current primary before offering a reload (VE-29505). */
  const openedMediaAssetId = session?.mediaAssetId ?? null;
  /**
   * Whether a PII task is EXPECTED but not yet visible.
   *
   * True only after a re-run this saga watched start — the one case where we
   * know a task exists before discovery can see it. Everywhere else "discovery
   * found no task" is a real answer, and treating it as a wait is what made a
   * document neither engine had run report PII as in progress: the awaiting
   * branch announces `PII_DETECTION_PROCESSING`, which is the spinner on the
   * Run button.
   */
  let expectPiiTask = false;
  /** The OCR counterpart, set when a run started from the page notice. */
  let expectOcrTask = false;

  /**
   * Point one engine's watch straight at the task the create response named.
   *
   * A re-run used to null `discovery` outright, which leaves that engine with
   * neither a result it may trust nor a task id — the one state that forces
   * FULL re-discovery, paging the TDO's whole task list 100 rows at a time,
   * every tick until the new task becomes queryable. That is the exact window
   * "discover once, then poll by id" exists to avoid paying in, and a re-run is
   * the case that reliably lands in it.
   *
   * The create response already knew the answer. Where it did not — an API that
   * returned no task records — the old behaviour stands: null, and go looking.
   *
   * The OTHER engine's work is preserved, so reviving one watch does not cost
   * the other its task id.
   */
  const watchTaskById = (engine: 'ocr' | 'pii', taskId?: string) => {
    if (!taskId) {
      discovery = null;
      return;
    }
    const base: EngineDiscovery = discovery ?? { ocr: NO_WORK, pii: NO_WORK };
    discovery = {
      ...base,
      // No status yet — the task exists but may not answer for a moment, which
      // `expect*Task` is what covers. No `createdDateTime` either: a task
      // created a second ago cannot be stalled, and asserting a time here
      // would be inventing one.
      [engine]: {
        resultExists: false,
        taskId,
        status: null,
        createdDateTime: null,
      },
    };
  };

  // The loop is fenced so the channel is CLOSED on every exit — the document
  // closing, a reopen cancelling this watch, or a throw. An `actionChannel`
  // registers a subscriber on the store and buffers into it forever; left open,
  // every document a reviewer visits added one more subscriber, each still
  // matching and buffering re-runs for a document nobody has open. `takeLatest`
  // cancels the saga, which is not the same as releasing what it allocated.
  try {
    while (true) {
      if (!ocr.active && !pii.active) {
        // Settled: park rather than return, so a re-run reuses this poller.
        const { closed, rerun } = yield* race({
          rerun: take(rerunChannel),
          closed: take(CLOSE_DOCUMENT),
        });
        if (closed) {
          return;
        }
        // A PII run that CHAINS OCR revives both watches — see `withOcr`.
        const ocrRerun =
          rerun?.type === RUN_OCR_DETECTION_SUCCESS.type ||
          !!(rerun as RerunAction | undefined)?.payload?.withOcr;
        const rerunTasks = (rerun as RerunAction | undefined)?.payload;
        if (ocrRerun && ocrEngineId) {
          ocr = initialOcrWatchState();
          // A fresh budget for finding the new run's task — all `firstReading`
          // still means.
          ocrFirstReading = true;
          // Whatever this session is rendering predates the run just started, so
          // its output will be a file the reviewer has not seen.
          ocrPinnedOutput = false;
          watchTaskById('ocr', rerunTasks?.ocrTaskId);
          expectOcrTask = true;
          trustOcrResult = false;
        }
        if (rerun?.type === RUN_PII_DETECTION_SUCCESS.type && piiEngineId) {
          pii = initialPiiWatchState();
          // The user asked for this run, so "no results" is a failure, not an
          // offer to run it.
          piiFirstReading = false;
          // A new task exists; watch it by id, and stop trusting the old asset.
          watchTaskById('pii', rerunTasks?.piiTaskId);
          trustPiiResult = false;
          expectPiiTask = true;
        }
        continue;
      }

      if (started) {
        const { closed, rerun } = yield* race({
          // The ordinary cadence, EXCEPT after a failed read, where it backs
          // off: an outage synchronises every open editor in the org onto the
          // same schedule, and hammering a struggling API from all of them is
          // how a blip becomes the outage. `consecutiveFetchErrors` is zeroed
          // by any successful read, so the interval returns to normal on its
          // own.
          tick: delay(
            consecutiveFetchErrors > 0
              ? retryBackoffMs(consecutiveFetchErrors)
              : POLL_INTERVAL_MS
          ),
          closed: take(CLOSE_DOCUMENT),
          rerun: take(rerunChannel),
        });
        if (closed) {
          return;
        }
        const rerunWithOcr =
          rerun?.type === RUN_OCR_DETECTION_SUCCESS.type ||
          !!(rerun as RerunAction | undefined)?.payload?.withOcr;
        const midTickTasks = (rerun as RerunAction | undefined)?.payload;
        if (rerunWithOcr && ocrEngineId) {
          ocr = initialOcrWatchState();
          ocrFirstReading = true;
          ocrPinnedOutput = false;
          consecutiveFetchErrors = 0;
          watchTaskById('ocr', midTickTasks?.ocrTaskId);
          expectOcrTask = true;
          // As in the parked branch above, and for the same reason: a failed run
          // keeps its partial manifest, so the asset still on the TDO is the
          // PREVIOUS run's. This branch is reached whenever the OTHER engine is
          // still active — PII running behind a failed OCR is exactly the case
          // Run Again is offered in.
          trustOcrResult = false;
        }
        if (rerun?.type === RUN_PII_DETECTION_SUCCESS.type && piiEngineId) {
          pii = initialPiiWatchState();
          piiFirstReading = false;
          consecutiveFetchErrors = 0;
          watchTaskById('pii', midTickTasks?.piiTaskId);
          trustPiiResult = false;
          expectPiiTask = true;
        }
      }
      started = true;

      const token = yield* select(selectToken);
      const { apiRoot, graphQLEndpoint, veritoneAppId } =
        yield* select(selectGraphQLConfig);

      /**
       * Re-discover rather than poll when an active engine has neither a result
       * nor a task to watch — its task row may have appeared since. Bounded in
       * practice by the step functions' own `MAX_POLLS_AWAITING_*`, which settle
       * the engine once they give up looking.
       */
      const outstanding = (
        engineId: string | null,
        active: boolean,
        work: EngineDiscovery['ocr'] | undefined
      ) => !!engineId && active && !work?.resultExists && !work?.taskId;

      const needsDiscovery =
        !discovery ||
        outstanding(ocrEngineId, ocr.active, discovery.ocr) ||
        outstanding(piiEngineId, pii.active, discovery.pii);

      try {
        if (needsDiscovery) {
          discovery = yield* call(
            serviceDiscoverEngineWork,
            tdoId,
            { ocrEngineId, piiEngineId },
            token,
            apiRoot,
            graphQLEndpoint,
            veritoneAppId,
            // A re-run's own output does not exist yet; the asset still on the
            // TDO is the previous run's, so this engine must be judged by its
            // task alone until that task is found.
            { ocr: trustOcrResult, pii: trustPiiResult }
          );
        } else if (discovery) {
          const known: EngineDiscovery = discovery;
          const statuses: { ocr: string | null; pii: string | null } =
            yield* call(
              serviceFetchTaskStatuses,
              { ocr: known.ocr.taskId, pii: known.pii.taskId },
              token,
              apiRoot,
              graphQLEndpoint,
              veritoneAppId
            );
          discovery = {
            ocr: { ...known.ocr, status: statuses.ocr },
            pii: { ...known.pii, status: statuses.pii },
          };
        }
      } catch (e: unknown) {
        if (yield* cancelled()) {
          return;
        }
        consecutiveFetchErrors += 1;
        /**
         * A TASK IN HAND CHANGES WHAT AN OUTAGE MEANS. Without one, a failed
         * read is all we know and thirty seconds is a reasonable time to stop
         * claiming to be checking. With one, a real run exists and is almost
         * certainly still running — so the watch waits out a far longer outage
         * rather than reporting on a job it simply cannot see.
         */
        const ocrTaskKnown = !!discovery?.ocr.taskId;
        const piiTaskKnown = !!discovery?.pii.taskId;
        const budget =
          (ocr.active && ocrTaskKnown) || (pii.active && piiTaskKnown)
            ? MAX_CONSECUTIVE_FETCH_ERRORS_WITH_TASK
            : MAX_CONSECUTIVE_FETCH_ERRORS;
        const givingUp = consecutiveFetchErrors >= budget;

        // Announce on the first pass's first failure, so `checking` does not
        // outlive it, and again on giving up, which is the final answer. Not in
        // between (noise), and not on a re-run's first failure — its state is
        // `processing`, and claiming `not-run` while the job is live is worse
        // than the spinner.
        //
        // WHAT it announces depends on whether a task was ever found.
        // `stepOcrWatchError` / `stepPiiWatchError` say "nothing is known" and
        // "it was never run" — both defensible when we never saw a task, and
        // both false once we have. `PII_NOT_RUN` over a live run is the worst
        // of them: it offers the Run button for a job already in flight, and
        // the reviewer starts a second one. A run we watched and then lost is
        // reported as timed out, which is what actually happened.
        if (
          ocr.active &&
          ((ocrFirstReading && !ocrFallbackAnnounced) || givingUp)
        ) {
          if (ocrTaskKnown) {
            yield* put(OCR_TIMED_OUT({ tdoId }));
          } else {
            yield* stepOcrWatchError(tdoId, e);
          }
          ocrFallbackAnnounced = true;
        }
        if (
          pii.active &&
          ((piiFirstReading && !piiFallbackAnnounced) || givingUp)
        ) {
          if (piiTaskKnown) {
            yield* put(PII_TIMED_OUT({ tdoId }));
          } else {
            yield* stepPiiWatchError(tdoId, e);
          }
          piiFallbackAnnounced = true;
        }
        if (!givingUp) {
          // This loop runs for the whole OCR duration; one bad read must not cost
          // the reload offer. The wait before the retry is the loop's own, backed
          // off — see the `delay` at the top.
          continue;
        }
        ocr = SETTLED;
        pii = SETTLED;
        // A re-run rejoining this loop gets its own budget.
        consecutiveFetchErrors = 0;
        continue;
      }
      consecutiveFetchErrors = 0;
      // The re-run's task has ANSWERED; stop expecting one.
      //
      // On its status, not on its id: since a re-run seeds the id from the
      // create response, an id alone no longer means the task row is queryable.
      // Clearing on the id would make the very first poll of a fresh task read
      // its empty status as a real answer — `not-run` for OCR, "finished with
      // no results" for PII — on a run that had only just started.
      if (discovery?.pii.status) {
        expectPiiTask = false;
      }
      if (discovery?.ocr.status) {
        expectOcrTask = false;
      }
      if (!discovery) {
        // Unreachable: the try above either assigns it or continues. Narrowing
        // only — never fall through to the steps without a reading.
        continue;
      }

      // Fenced per engine: the steps also load the manifest / results asset, both
      // of which throw on a GraphQL error or a non-200, and one engine's failure
      // must not resolve the other's state.
      if (ocr.active && ocrEngineId) {
        try {
          ocr = yield* stepOcrWatch(
            ocr,
            engineStatusFromWork(discovery.ocr, expectOcrTask),
            {
              tdoId,
              engineId: ocrEngineId,
              firstReading: ocrFirstReading,
              pinnedOcrOutput: ocrPinnedOutput,
              openedMediaAssetId,
            }
          );
        } catch (e: unknown) {
          if (yield* cancelled()) {
            return;
          }
          yield* stepOcrWatchError(tdoId, e);
          // Settled, not retried: another tick asks the same question.
          ocr = SETTLED;
        }
        ocrFirstReading = false;
      }
      if (pii.active && piiEngineId && pdfUrl) {
        try {
          pii = yield* stepPiiWatch(
            pii,
            engineStatusFromWork(discovery.pii, expectPiiTask),
            {
              tdoId,
              engineId: piiEngineId,
              pdfUrl,
              firstReading: piiFirstReading,
            }
          );
        } catch (e: unknown) {
          if (yield* cancelled()) {
            return;
          }
          yield* stepPiiWatchError(tdoId, e);
          pii = SETTLED;
        }
        piiFirstReading = false;
      }
    }
  } finally {
    rerunChannel.close();
  }
}

export function* engineStatusSaga() {
  // takeLatest so opening another document cancels this watch outright.
  yield* takeLatest(OPEN_DOCUMENT_SUCCESS, watchEngineStatus);
}
